// page-meeting-draft-actions.ts — Table « Nouvelles actions » de l'onglet
// Notes de la fiche réunion : brouillons d'actions saisis pendant la réunion
// (titre, approbateur, échéance), auto-sauvegardés champ par champ et
// rafraîchis par polling.
//
// Première tranche de l'extraction des closures de `renderMeetingPage`
// (audit dette technique 2026-09-07, point 16). Avant, les six fonctions de
// ce module vivaient dans la god-function et partageaient son état par
// capture ; rien n'était testable sans monter la page entière. L'état dont
// elles ont besoin est maintenant explicite (`DraftTableContext`) : l'id de la
// réunion, la liste des utilisateurs et le verrou « réunion fermée ».
//
// Les rendus (`renderDraftActionsTable`) et l'empreinte (`fingerprintDrafts`)
// sont purs et testés ; le picker d'approbateur et le câblage sont testés sur
// happy-dom. `api`, `esc`, `t` et `toast` restent des globales ambiantes
// (cf. types/core-globals.d.ts), comme dans les autres modules extraits.

export interface DraftActionItem {
  id: number; // 0 = ligne d'ajout (vide)
  text: string;
  approver_id: number | null;
  approver?: { id: number; trigram?: string | null; name: string } | null;
  deadline: string | null;
  // Sans ces 5 champs (backend, 2026-08-07) les colonnes RACI et Projet du
  // tableau des brouillons restent vides même après un rattachement réussi.
  priority?: number | null;
  performers?: number[];
  consulted?: number[];
  informed?: number[];
  project_ids?: number[];
}

export interface DraftUser {
  id: number;
  trigram: string;
  name: string;
}

export interface DraftTableContext {
  meetingId: number;
  users: ReadonlyArray<DraftUser>;
  /** Réunion fermée : tout en lecture seule, pas de ligne d'ajout. */
  isClosed: boolean;
}

/** Id DOM du conteneur de la table, posé par l'onglet Notes. */
export const DRAFT_HOST_ID = 'mp-draft-host';
const PICKER_ID = 'mp-approver-picker';

type DraftField = 'text' | 'approver_id' | 'deadline';

/** Empreinte de la liste rendue. Le polling ne re-rend que si elle change :
 * un swap d'innerHTML inutile recréait les <input> et donnait l'impression
 * d'un bref passage en lecture seule. */
export function fingerprintDrafts(items: ReadonlyArray<DraftActionItem>): string {
  return items
    .map((it) => `${it.id}|${it.text || ''}|${it.approver_id || 0}|${it.deadline || ''}`)
    .join('§');
}

export function renderDraftActionsTable(
  items: ReadonlyArray<DraftActionItem>,
  users: ReadonlyArray<DraftUser>,
  isClosed: boolean,
): string {
  const approverCell = (rowId: number, approverId: number | null): string => {
    const u = approverId ? users.find((x) => x.id === approverId) : null;
    const trigram = u ? u.trigram : '';
    const fullName = u ? u.name : '';
    const disabledAttr = isClosed ? ' disabled' : '';
    const cursor = isClosed ? 'default' : 'pointer';
    return `<button type="button" data-approver-row="${rowId}"${disabledAttr}
        title="${esc(fullName || t('pageMeeting.notes.approverPlaceholder'))}"
        style="display:inline-flex;align-items:center;gap:4px;padding:3px 8px;border:1px solid var(--border2);border-radius:4px;background:#fff;cursor:${cursor};font-size:12px;font-weight:${u ? '600' : '400'};color:${u ? 'var(--text)' : 'var(--text3)'};min-width:60px${isClosed ? ';opacity:0.7' : ''}">
        ${u ? esc(trigram || '?') : '—'}
      </button>`;
  };
  const row = (it: DraftActionItem): string => {
    const isNew = it.id === 0;
    const titleVal = esc(it.text || '');
    const deadlineVal = it.deadline || '';
    const approverId = it.approver_id ?? null;
    const lockedAttr = isClosed ? ' disabled readonly' : '';
    return `<tr>
        <td style="padding:4px 6px">
          <input type="text" data-draft-row="${it.id}" data-draft-field="text"
            value="${titleVal}" placeholder="${esc(t('pageMeeting.notes.titlePlaceholder'))}"${lockedAttr}
            style="width:100%;padding:4px 6px;border:1px solid var(--border2);border-radius:4px;font-size:12px${isClosed ? ';background:var(--bg2)' : ''}">
        </td>
        <td style="padding:4px 6px;width:90px">
          ${approverCell(it.id, approverId)}
        </td>
        <td style="padding:4px 6px;width:140px">
          <input type="date" data-draft-row="${it.id}" data-draft-field="deadline"
            value="${deadlineVal}"${lockedAttr}
            style="width:100%;padding:4px 6px;border:1px solid var(--border2);border-radius:4px;font-size:12px${isClosed ? ';background:var(--bg2)' : ''}">
        </td>
        <td style="padding:4px 6px;width:32px;text-align:center">
          ${
            isNew || isClosed
              ? ''
              : `<button type="button" class="btn btn-danger btn-icon" data-draft-delete="${it.id}" title="${esc(t('common.delete'))}">✕</button>`
          }
        </td>
      </tr>`;
  };
  // En réunion fermée, on supprime la ligne vide d'ajout en bas.
  const newRowHtml = isClosed
    ? ''
    : row({ id: 0, text: '', approver_id: null, deadline: null } as DraftActionItem);
  return `<div class="table-wrap"><table class="table" style="font-size:12px">
      <thead><tr>
        <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px">${esc(t('pageMeeting.notes.colTitle'))}</th>
        <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px">${esc(t('pageMeeting.notes.colApprover'))}</th>
        <th style="text-align:left;padding:4px 6px;color:var(--text3);font-weight:600;font-size:11px">${esc(t('pageMeeting.notes.colDeadline'))}</th>
        <th></th>
      </tr></thead>
      <tbody>
        ${items.map(row).join('')}
        ${newRowHtml}
      </tbody>
    </table></div>`;
}

/** Picker custom pour l'approbateur (single-user) : recherche + liste + CTA
 * Ajouter. Ancré sous le bouton trigramme cliqué. Pas de showRaciPicker, qui
 * est multi-select à la base. */
export function showApproverPicker(
  users: ReadonlyArray<DraftUser>,
  anchorBtn: HTMLElement,
  currentId: number | null,
  onSelect: (userId: number | null) => void,
): void {
  document.querySelectorAll('#' + PICKER_ID).forEach((el) => el.remove());

  let search = '';
  let selectedId: number | null = currentId;

  const renderList = (): string => {
    const q = search.toLowerCase();
    const list = users.filter(
      (u) => !q || u.name.toLowerCase().includes(q) || (u.trigram || '').toLowerCase().includes(q),
    );
    if (!list.length) {
      return `<div style="color:var(--text3);font-size:12px;padding:12px;text-align:center">${esc(t('pageMeeting.notes.approverEmpty'))}</div>`;
    }
    return list
      .map((u) => {
        const sel = u.id === selectedId;
        return `<div class="mp-app-row" data-uid="${u.id}"
            style="display:flex;align-items:center;gap:8px;padding:6px 10px;cursor:pointer;font-size:12px;${sel ? 'background:var(--accent-bg);' : ''}">
            <span style="font-weight:600;min-width:48px;color:var(--text2)">${esc(u.trigram || '')}</span>
            <span>${esc(u.name)}</span>
          </div>`;
      })
      .join('');
  };

  const dropdown = document.createElement('div');
  dropdown.id = PICKER_ID;
  dropdown.style.cssText = [
    'position:fixed',
    'z-index:1000',
    'background:var(--bg2)',
    'border:1px solid var(--border2)',
    'border-radius:var(--radius)',
    'box-shadow:0 8px 24px rgba(0,0,0,.15)',
    'width:280px',
    'max-height:380px',
    'display:flex',
    'flex-direction:column',
    'overflow:hidden',
  ].join(';');
  dropdown.innerHTML = `
      <div style="padding:8px;border-bottom:1px solid var(--border)">
        <input type="text" class="mp-app-search" placeholder="${esc(t('pageMeeting.notes.approverSearch'))}"
          autocomplete="off"
          style="width:100%;font-size:12px;padding:6px 10px;border:1px solid var(--border2);border-radius:var(--radius)">
      </div>
      <div class="mp-app-list" style="flex:1;overflow-y:auto">${renderList()}</div>
      <div style="padding:8px 10px;border-top:1px solid var(--border);display:flex;justify-content:space-between;gap:6px">
        <button type="button" class="btn btn-link btn-sm mp-app-clear" style="font-size:11px">${esc(t('pageMeeting.notes.approverClear'))}</button>
        <button type="button" class="btn btn-primary btn-sm mp-app-add">${esc(t('actionModal.picker.btnAdd'))}</button>
      </div>`;
  document.body.appendChild(dropdown);

  // Positionnement sous le bouton, calé à droite si débord.
  const rect = anchorBtn.getBoundingClientRect();
  const w = 280;
  let left = rect.left;
  if (left + w > window.innerWidth - 8) left = window.innerWidth - w - 8;
  let top = rect.bottom + 4;
  if (top + 380 > window.innerHeight - 8) top = Math.max(8, rect.top - 380 - 4);
  dropdown.style.top = top + 'px';
  dropdown.style.left = left + 'px';

  const listEl = dropdown.querySelector<HTMLElement>('.mp-app-list');
  const searchInput = dropdown.querySelector<HTMLInputElement>('.mp-app-search');
  const addBtn = dropdown.querySelector<HTMLButtonElement>('.mp-app-add');
  const clearBtn = dropdown.querySelector<HTMLButtonElement>('.mp-app-clear');

  const close = (): void => {
    document.removeEventListener('mousedown', onOutside, true);
    document.removeEventListener('keydown', onEsc, true);
    dropdown.remove();
  };
  const onOutside = (e: MouseEvent): void => {
    const target = e.target as Node | null;
    if (!target) return;
    if (dropdown.contains(target) || target === anchorBtn || anchorBtn.contains(target)) return;
    close();
  };
  const onEsc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  setTimeout(() => {
    document.addEventListener('mousedown', onOutside, true);
    document.addEventListener('keydown', onEsc, true);
  }, 0);

  searchInput?.addEventListener('input', (e) => {
    search = (e.target as HTMLInputElement).value;
    if (listEl) listEl.innerHTML = renderList();
  });
  listEl?.addEventListener('click', (e) => {
    const row = (e.target as HTMLElement).closest<HTMLElement>('.mp-app-row');
    if (!row) return;
    selectedId = parseInt(row.dataset['uid'] || '0') || null;
    // Repeint l'état actif sans re-render complet.
    if (listEl) listEl.innerHTML = renderList();
  });
  addBtn?.addEventListener('click', () => {
    onSelect(selectedId);
    close();
  });
  clearBtn?.addEventListener('click', () => {
    onSelect(null);
    close();
  });

  setTimeout(() => searchInput?.focus(), 0);
}

export interface DraftActionsTable {
  /** (Re)charge et (re)rend la table. `preserveFocus=true` est passé par le
   * polling : si l'utilisateur saisit dans la table ou si le picker est
   * ouvert, le cycle est sauté et retenté au prochain tick. */
  load(preserveFocus: boolean): Promise<void>;
}

/** Instancie la table pour une réunion. L'empreinte de la dernière liste
 * rendue vit dans cette fermeture, pas dans la page. */
export function createDraftActionsTable(ctx: DraftTableContext): DraftActionsTable {
  const { meetingId, users, isClosed } = ctx;
  let lastFingerprint = '';

  async function load(preserveFocus: boolean): Promise<void> {
    const host = document.getElementById(DRAFT_HOST_ID);
    if (!host) return;
    if (preserveFocus) {
      const active = document.activeElement as HTMLElement | null;
      if (active && host.contains(active)) return;
      if (document.getElementById(PICKER_ID)) return;
    }

    let items: DraftActionItem[];
    try {
      items = (await api.listMeetingDraftActions(meetingId)) as DraftActionItem[];
    } catch (err: unknown) {
      if (!preserveFocus) {
        host.innerHTML = `<div class="error">${esc(err instanceof Error ? err.message : t('common.error'))}</div>`;
      }
      return;
    }

    // Skip silencieusement si rien n'a changé — évite le re-render qui
    // détruit/recrée les <input>.
    const fp = fingerprintDrafts(items);
    if (preserveFocus && fp === lastFingerprint) {
      return;
    }
    lastFingerprint = fp;

    host.innerHTML = renderDraftActionsTable(items, users, isClosed);
    wire(host, items);
  }

  function wire(host: HTMLElement, items: ReadonlyArray<DraftActionItem>): void {
    // Auto-save sur sortie de champ. Pour la ligne d'ajout (id=0), on
    // accumule les changements et on POST dès qu'un champ a du contenu.
    const pendingNew: { text: string; approver_id: number | null; deadline: string | null } = {
      text: '',
      approver_id: null,
      deadline: null,
    };

    // Mutateur factorisé : applique un changement d'un champ pour rowId,
    // crée la nouvelle action si rowId=0 et qu'il y a du contenu, sinon
    // PATCH la ligne existante.
    const applyChange = (rowId: number, field: DraftField, next: string | number | null): void => {
      if (rowId === 0) {
        if (field === 'text') pendingNew.text = (next as string) || '';
        else if (field === 'approver_id')
          pendingNew.approver_id = next === null ? null : (next as number);
        else if (field === 'deadline') pendingNew.deadline = (next as string) || null;
        const hasContent = pendingNew.text || pendingNew.approver_id || pendingNew.deadline;
        if (!hasContent) return;
        void (async () => {
          try {
            await api.createMeetingDraftAction(meetingId, {
              text: pendingNew.text || null,
              approver_id: pendingNew.approver_id,
              deadline: pendingNew.deadline,
            });
            pendingNew.text = '';
            pendingNew.approver_id = null;
            pendingNew.deadline = null;
            await load(false);
          } catch (err: unknown) {
            toast(err instanceof Error ? err.message : t('common.error'), 'error');
          }
        })();
        return;
      }
      const item = items.find((x) => x.id === rowId);
      if (!item) return;
      const current =
        field === 'text'
          ? item.text || null
          : field === 'approver_id'
            ? (item.approver_id ?? null)
            : item.deadline || null;
      if (current === next) return;
      const payload: {
        text?: string | null;
        approver_id?: number | null;
        deadline?: string | null;
      } = { [field]: next } as never;
      void (async () => {
        try {
          await api.patchMeetingDraftAction(meetingId, rowId, payload);
        } catch (err: unknown) {
          toast(err instanceof Error ? err.message : t('common.error'), 'error');
        }
      })();
    };

    // Inputs text + date : auto-save blur/change.
    host.querySelectorAll<HTMLInputElement>('[data-draft-row][data-draft-field]').forEach((el) => {
      const rowId = parseInt(el.dataset['draftRow'] || '0');
      const field = el.dataset['draftField'] as 'text' | 'deadline';
      const eventName = el.getAttribute('type') === 'date' ? 'change' : 'blur';
      el.addEventListener(eventName, () => {
        if (field === 'text') {
          applyChange(rowId, 'text', el.value.trim() || null);
        } else if (field === 'deadline') {
          applyChange(rowId, 'deadline', el.value || null);
        }
      });
    });

    // Bouton trigramme approbateur → ouvre le picker custom (search + add).
    host.querySelectorAll<HTMLButtonElement>('[data-approver-row]').forEach((btn) => {
      const rowId = parseInt(btn.dataset['approverRow'] || '0');
      const item = items.find((x) => x.id === rowId);
      const currentId = item?.approver_id ?? null;
      btn.addEventListener('click', () => {
        showApproverPicker(users, btn, currentId, (nextId) => {
          applyChange(rowId, 'approver_id', nextId);
          // Optimistic UI : mettre à jour visuellement le bouton sans
          // attendre le polling.
          const u = nextId ? users.find((x) => x.id === nextId) : null;
          if (u) {
            btn.textContent = u.trigram || '?';
            btn.title = u.name;
            btn.style.fontWeight = '600';
            btn.style.color = 'var(--text)';
          } else {
            btn.textContent = '—';
            btn.title = t('pageMeeting.notes.approverPlaceholder');
            btn.style.fontWeight = '400';
            btn.style.color = 'var(--text3)';
          }
        });
      });
    });

    host.querySelectorAll<HTMLButtonElement>('[data-draft-delete]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const id = parseInt(btn.dataset['draftDelete'] || '0');
        if (!id) return;
        void (async () => {
          try {
            await api.deleteMeetingDraftAction(meetingId, id);
            await load(false);
          } catch (err: unknown) {
            toast(err instanceof Error ? err.message : t('common.error'), 'error');
          }
        })();
      });
    });
  }

  return { load };
}
